/* ============================================================
   dn-quickshop.js
   DeskNeko 4.2 (Claude) — product card Quick View.

   Merchant 2026-07-16: "Can we create a Quick View lightbox for all
   products? It should look similar to the actual product page but more
   compact. On mobile it can look kinda like what we did for the wishlist"

   WHAT IT DOES
     Progressive enhancement on the card's EXISTING "View options" control
     (snippets/product-item-add-to-cart.liquid -> .product-item-view-link).
     That control stays a real <a href="{{ product.url }}">, so:
       - JS missing / failed / still loading  -> the card navigates to the
         real product page, exactly as it does today;
       - JS present -> the click opens a compact Quick View dialog instead.
     No second control is added to the card. The card image and title keep
     linking straight to the product page, so there is exactly ONE
     quick-view entry point per card and nothing competes with it.

   WHY DELEGATION
     Fully event-delegated on `document`, so cards injected later by
     assets/dn-filter-test.js (filtering / "Load more") work with no
     re-init and that 63KB file is never touched. Verified 2026-07-16:
     dn-filter-test.js contains no client-side card template — it injects
     SERVER-rendered section HTML, so every injected card carries
     .product-item-view-link (page-2 markup checked: 24/24 cards, 0 missing).

   RELATION TO THE WISHLIST QUICK-SHOP
     assets/dn-wishlist-quickshop.js is the merchant-approved in-drawer
     pattern this mirrors. It exists ONLY as minified bytes on the theme
     (no source anywhere on disk), exposes nothing, and is hard-bound to
     #offcanvas-wishlist, so it has no seam to extend from. This file is
     written as the readable, canonical implementation and deliberately
     mirrors that component's semantics 1:1 (money format, variant match,
     availability labels, swipe threshold, error copy, add-to-cart path)
     so the two can be collapsed into one core without behavior drift.
     See the report / follow-up task for the migration path.

   CART
     Add-to-cart reuses the theme's own existing path and adds nothing:
     POST /cart/add.js -> window.refreshCartContents(response) -> open
     #offcanvas-cart. assets/cart.js is NOT touched and NOT bypassed.
   ============================================================ */
(function () {
  if (window.dnQuickShopInit) return;
  window.dnQuickShopInit = true;

  var TRIGGER = '.product-item-view-link';
  var GRID = '.product-list';
  var SWIPE_PX = 45;    // mirrors dn-wishlist-quickshop
  var SWIPE_RATIO = 1.4;

  var reduceMotion = false;
  try {
    reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  var root = null;      // dialog root
  var el = {};          // cached child nodes
  var items = [];       // [{handle, url}] for the grid we opened from
  var index = -1;
  var product = null;   // current /products/{handle}.js payload
  var variant = null;
  var openerEl = null;  // the trigger we must return focus to
  var lastFocused = null;
  var token = 0;        // stale-response guard
  var isOpen = false;
  var hideTimer = null;
  var inerted = [];

  var strings = (window.theme && window.theme.product) || {};

  var ICON_PREV = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>';
  var ICON_NEXT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>';
  var ICON_CLOSE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';

  function t(key, fallback) {
    return strings[key] || fallback;
  }

  function handleFromUrl(href) {
    if (!href) return '';
    var m = String(href).match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  /* Money formatting — identical rules to the wishlist quick-shop. */
  function money(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      (window.theme && window.theme.currency) || '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        var s = window.Shopify.formatMoney(cents);
        return cur ? s + ' ' + cur : s;
      } catch (e) {}
    }
    var v = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: cur || 'EUR'
      }).format(v);
    } catch (e) {
      return v.toFixed(2) + (cur ? ' ' + cur : '');
    }
  }

  function resize(src, spec) {
    if (!src) return '';
    if (window.Shopify && typeof window.Shopify.resizeImage === 'function') {
      try { return window.Shopify.resizeImage(src, spec, 'center'); } catch (e) {}
    }
    return src;
  }

  /* ---------- build ---------- */
  function build() {
    root = document.createElement('div');
    root.className = 'dn-quickshop';
    root.hidden = true;
    root.innerHTML = [
      '<div class="dn-quickshop__backdrop" data-qs-close></div>',
      '<div class="dn-quickshop__dialog" role="dialog" aria-modal="true" aria-labelledby="dn-quickshop-title">',
        '<div class="dn-quickshop__bar">',
          '<p class="dn-quickshop__counter" aria-live="polite"></p>',
          '<span class="dn-quickshop__dots" aria-hidden="true"></span>',
          '<button type="button" class="dn-quickshop__close" data-qs-close aria-label="Close quick view">' + ICON_CLOSE + '</button>',
        '</div>',
        '<div class="dn-quickshop__scroll">',
          '<div class="dn-quickshop__panel">',
            '<div class="dn-quickshop__media">',
              '<button type="button" class="dn-quickshop__nav dn-quickshop__nav--prev" aria-label="Previous product">' + ICON_PREV + '</button>',
              '<button type="button" class="dn-quickshop__nav dn-quickshop__nav--next" aria-label="Next product">' + ICON_NEXT + '</button>',
              '<img class="dn-quickshop__img" alt="" width="800" height="800" decoding="async">',
            '</div>',
            '<div class="dn-quickshop__info">',
              '<h2 class="dn-quickshop__title" id="dn-quickshop-title"></h2>',
              '<div class="dn-quickshop__price"></div>',
              '<form class="dn-quickshop__form" action="/cart/add" method="post" novalidate>',
                '<input type="hidden" name="id" value="">',
                '<div class="dn-quickshop__options" hidden></div>',
                '<button type="submit" class="dn-quickshop__atc"><span class="dn-quickshop__atc-text">Add to cart</span></button>',
                '<p class="dn-quickshop__error" role="alert" hidden></p>',
              '</form>',
              '<a class="dn-quickshop__view" href="#">View full product page <span aria-hidden="true">&rarr;</span></a>',
            '</div>',
          '</div>',
          '<div class="dn-quickshop__status" hidden><span class="dn-quickshop__spin"></span><span class="dn-quickshop__status-text">Loading</span></div>',
        '</div>',
      '</div>'
    ].join('');
    document.body.appendChild(root);

    el.dialog = root.querySelector('.dn-quickshop__dialog');
    el.counter = root.querySelector('.dn-quickshop__counter');
    el.dots = root.querySelector('.dn-quickshop__dots');
    el.close = root.querySelector('.dn-quickshop__close');
    el.panel = root.querySelector('.dn-quickshop__panel');
    el.prev = root.querySelector('.dn-quickshop__nav--prev');
    el.next = root.querySelector('.dn-quickshop__nav--next');
    el.img = root.querySelector('.dn-quickshop__img');
    el.title = root.querySelector('.dn-quickshop__title');
    el.price = root.querySelector('.dn-quickshop__price');
    el.form = root.querySelector('.dn-quickshop__form');
    el.id = root.querySelector('input[name="id"]');
    el.options = root.querySelector('.dn-quickshop__options');
    el.atc = root.querySelector('.dn-quickshop__atc');
    el.atcText = root.querySelector('.dn-quickshop__atc-text');
    el.error = root.querySelector('.dn-quickshop__error');
    el.view = root.querySelector('.dn-quickshop__view');
    el.status = root.querySelector('.dn-quickshop__status');
    el.statusText = root.querySelector('.dn-quickshop__status-text');
    el.scroll = root.querySelector('.dn-quickshop__scroll');

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-qs-close]')) { e.preventDefault(); close(); }
    });
    el.prev.addEventListener('click', function () { step(-1); });
    el.next.addEventListener('click', function () { step(1); });
    el.form.addEventListener('submit', addToCart);
    root.addEventListener('keydown', onKeydown);
    bindSwipe();
  }

  function bindSwipe() {
    var media = root.querySelector('.dn-quickshop__media');
    if (!media) return;
    var x0 = 0, y0 = 0, live = false;
    media.addEventListener('touchstart', function (e) {
      if (items.length < 2 || e.touches.length !== 1) { live = false; return; }
      live = true; x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
    }, { passive: true });
    media.addEventListener('touchend', function (e) {
      if (!live) return;
      live = false;
      var tch = e.changedTouches && e.changedTouches[0];
      if (!tch) return;
      var dx = tch.clientX - x0, dy = tch.clientY - y0;
      if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > SWIPE_RATIO * Math.abs(dy)) step(dx < 0 ? 1 : -1);
    }, { passive: true });
  }

  /* ---------- page behind the dialog ---------- */
  function setInert(on) {
    if (on) {
      inerted = [];
      Array.prototype.forEach.call(document.body.children, function (node) {
        if (node === root || node.nodeType !== 1) return;
        if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE' || node.tagName === 'LINK') return;
        node.setAttribute('inert', '');
        node.setAttribute('aria-hidden', 'true');
        inerted.push(node);
      });
      document.documentElement.classList.add('dn-quickshop-open');
    } else {
      inerted.forEach(function (node) {
        node.removeAttribute('inert');
        node.removeAttribute('aria-hidden');
      });
      inerted = [];
      document.documentElement.classList.remove('dn-quickshop-open');
    }
  }

  /* ---------- render ---------- */
  function status(on, text) {
    if (!el.status) return;
    if (!on) { el.status.hidden = true; return; }
    el.statusText.textContent = text || 'Loading';
    el.status.hidden = false;
    var spin = el.status.querySelector('.dn-quickshop__spin');
    if (spin) spin.hidden = !!text;
  }

  function showError(msg) {
    if (!el.error) return;
    if (!msg) { el.error.hidden = true; el.error.textContent = ''; return; }
    el.error.textContent = msg;
    el.error.hidden = false;
  }

  function setImage(src, alt) {
    el.img.src = resize(src, '800x800') || src || '';
    el.img.alt = alt || '';
  }

  function optionNames(p) {
    return (p.options || []).map(function (o, i) {
      return (o && typeof o === 'object') ? (o.name || 'Option ' + (i + 1)) : o;
    });
  }

  function matchVariant(p, values) {
    return p.variants.filter(function (v) {
      var o = v.options || [];
      if (o.length !== values.length) return false;
      for (var i = 0; i < values.length; i++) if (o[i] !== values[i]) return false;
      return true;
    })[0] || null;
  }

  function onOptionChange() {
    if (!product) return;
    var values = Array.prototype.map.call(el.options.querySelectorAll('select'), function (s) { return s.value; });
    applyVariant(product, matchVariant(product, values));
  }

  function applyVariant(p, v) {
    showError('');
    if (!v) {
      variant = null;
      el.id.value = '';
      el.atc.disabled = true;
      el.atcText.textContent = t('unavailable', 'Unavailable');
      el.view.setAttribute('href', p.url);
      return;
    }
    variant = v;
    el.id.value = String(v.id);

    el.price.textContent = '';
    var main = document.createElement('span');
    main.className = 'dn-quickshop__price-main';
    main.textContent = money(v.price);
    el.price.appendChild(main);
    if (v.compare_at_price && v.compare_at_price > v.price) {
      var cmp = document.createElement('span');
      cmp.className = 'dn-quickshop__price-compare';
      cmp.textContent = money(v.compare_at_price);
      el.price.appendChild(cmp);
    }

    if (v.available) {
      el.atc.disabled = false;
      el.atcText.textContent = t('addToCart', 'Add to cart');
    } else {
      el.atc.disabled = true;
      el.atcText.textContent = t('soldOut', 'Sold out');
    }

    if (v.featured_image && v.featured_image.src) {
      var suffix = (v.title && v.title !== 'Default Title') ? ', ' + v.title : '';
      setImage(v.featured_image.src, (p.title || '') + suffix);
    }
    el.view.setAttribute('href', p.url + '?variant=' + v.id);
  }

  function buildOptions(p, selected) {
    el.options.innerHTML = '';
    if (!p.variants || p.variants.length <= 1) { el.options.hidden = true; return; }
    el.options.hidden = false;
    var names = optionNames(p);
    var count = ((p.variants[0].options || []).length) || names.length;
    for (var i = 0; i < count; i++) {
      (function (idx) {
        var field = document.createElement('div');
        field.className = 'dn-quickshop__field';
        var id = 'dn-quickshop-opt-' + idx;
        var label = document.createElement('label');
        label.className = 'dn-quickshop__field-label';
        label.setAttribute('for', id);
        label.textContent = names[idx] || 'Option ' + (idx + 1);
        var sel = document.createElement('select');
        sel.className = 'dn-quickshop__select';
        sel.id = id;
        var seen = {};
        p.variants.forEach(function (v) {
          var val = (v.options || [])[idx];
          if (val == null || seen[val]) return;
          seen[val] = true;
          var opt = document.createElement('option');
          opt.value = val;
          opt.textContent = val;
          if (selected && (selected.options || [])[idx] === val) opt.selected = true;
          sel.appendChild(opt);
        });
        sel.addEventListener('change', onOptionChange);
        field.appendChild(label);
        field.appendChild(sel);
        el.options.appendChild(field);
      })(i);
    }
  }

  function pickVariant(p) {
    return p.variants.filter(function (v) { return v.available; })[0] || p.variants[0] || null;
  }

  function renderProduct(p, instant) {
    var paint = function () {
      root.classList.remove('is-loading');
      status(false);
      showError('');
      el.title.textContent = p.title || '';
      var v = pickVariant(p);
      setImage((v && v.featured_image && v.featured_image.src) || p.featured_image, p.title || '');
      buildOptions(p, v);
      applyVariant(p, v);
      el.panel.classList.remove('is-swapping');
    };
    if (instant || reduceMotion) paint();
    else { el.panel.classList.add('is-swapping'); window.setTimeout(paint, 140); }
  }

  function renderUnavailable(item) {
    root.classList.remove('is-loading');
    el.panel.classList.remove('is-swapping');
    // Only ever show a title we actually read off the card. Never invent one,
    // and never show a price/availability we could not load.
    el.title.textContent = (item && item.title) || '';
    el.price.textContent = '';
    el.options.hidden = true;
    el.options.innerHTML = '';
    el.id.value = '';
    el.atc.disabled = true;
    el.atcText.textContent = t('unavailable', 'Unavailable');
    setImage('', '');
    el.view.setAttribute('href', (item && item.url) || '/');
    status(true, 'This item could not be loaded. Open the full product page.');
  }

  function paintChrome() {
    var many = items.length > 1;
    el.counter.textContent = (index + 1) + ' of ' + items.length;
    el.prev.hidden = !many;
    el.next.hidden = !many;
    el.dots.textContent = '';
    if (many && items.length <= 8) {
      for (var i = 0; i < items.length; i++) {
        var d = document.createElement('span');
        d.className = 'dn-quickshop__dot' + (i === index ? ' is-active' : '');
        el.dots.appendChild(d);
      }
      el.dots.hidden = false;
    } else {
      el.dots.hidden = true;
    }
  }

  function show(i, instant) {
    var n = items.length;
    if (!n) { close(); return; }
    index = ((i % n) + n) % n;
    var item = items[index];
    paintChrome();
    root.classList.add('is-loading');
    if (!instant && !reduceMotion) el.panel.classList.add('is-swapping');
    status(true);
    showError('');
    var mine = ++token;
    fetch('/products/' + encodeURIComponent(item.handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (p) {
        if (mine !== token || !isOpen) return;
        product = p;
        renderProduct(p, instant);
      })
      .catch(function () {
        if (mine !== token || !isOpen) return;
        product = null;
        renderUnavailable(item);
      });
  }

  function step(dir) {
    if (items.length < 2) return;
    show(index + dir, false);
  }

  /* ---------- open / close ---------- */
  function collect(trigger) {
    var grid = (trigger.closest && trigger.closest(GRID)) || document;
    var links = grid.querySelectorAll(TRIGGER);
    var out = [];
    Array.prototype.forEach.call(links, function (a) {
      var h = handleFromUrl(a.getAttribute('href'));
      if (!h) return;
      // Real title straight off the card, used only as the fallback heading
      // if /products/{handle}.js cannot be loaded.
      var card = a.closest('.product-item');
      var titleEl = card && card.querySelector('.product-item-title');
      out.push({
        handle: h,
        url: a.getAttribute('href'),
        title: titleEl ? titleEl.textContent.trim() : '',
        el: a
      });
    });
    return out;
  }

  function open(trigger) {
    if (!root) build();
    items = collect(trigger);
    if (!items.length) return false;

    var start = 0;
    for (var i = 0; i < items.length; i++) {
      if (items[i].el === trigger) { start = i; break; }
    }

    openerEl = trigger;
    lastFocused = document.activeElement;
    isOpen = true;
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }

    root.hidden = false;
    void root.offsetWidth;          // commit the closed state before animating
    root.classList.add('is-open');
    setInert(true);
    if (el.scroll) el.scroll.scrollTop = 0;
    show(start, true);
    window.setTimeout(function () {
      if (isOpen && el.close) el.close.focus();
    }, reduceMotion ? 0 : 60);
    return true;
  }

  function close() {
    if (!isOpen || !root) return;
    isOpen = false;
    token++;
    root.classList.remove('is-open');
    setInert(false);

    var finish = function () { if (!isOpen) root.hidden = true; };
    if (reduceMotion) finish();
    else hideTimer = window.setTimeout(finish, 260);

    var back = (openerEl && openerEl.isConnected) ? openerEl :
      ((lastFocused && lastFocused.isConnected) ? lastFocused : null);
    if (back && typeof back.focus === 'function') {
      try { back.focus(); } catch (e) {}
    }
  }

  /* ---------- add to cart (reuses the theme's own path) ---------- */
  function addToCart(e) {
    e.preventDefault();
    var id = el.id.value;
    if (!id) return;
    var restore = el.atcText.textContent;
    showError('');
    el.atc.disabled = true;
    el.atc.setAttribute('aria-busy', 'true');
    el.atcText.innerHTML = '<span class="dn-quickshop__spin" aria-hidden="true"></span>' + t('addingToCart', 'Adding...');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (res) {
        var r = (typeof window.refreshCartContents === 'function') ? window.refreshCartContents(res) : null;
        return Promise.resolve(r).then(function () { return res; });
      })
      .then(function (res) {
        if (res && res.ok === false) throw new Error('add failed');
        el.atc.removeAttribute('aria-busy');
        el.atcText.textContent = t('addedToCart', 'Added');
        close();
        var bs = window.bootstrap;
        var cart = document.getElementById('offcanvas-cart');
        if (bs && bs.Offcanvas && cart) bs.Offcanvas.getOrCreateInstance(cart).show();
      })
      .catch(function () {
        // Never surface an internal error string to a shopper.
        el.atc.removeAttribute('aria-busy');
        el.atcText.textContent = restore;
        if (variant && variant.available) el.atc.disabled = false;
        showError('We could not add this to your cart. Please try again.');
      });
  }

  /* ---------- keyboard ---------- */
  function onKeydown(e) {
    if (!isOpen) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    if (e.key === 'Tab') {
      var nodes = root.querySelectorAll('a[href], button:not([disabled]), select, input, [tabindex]:not([tabindex="-1"])');
      var f = Array.prototype.filter.call(nodes, function (n) {
        return !n.hidden && n.offsetParent !== null && !n.closest('[hidden]');
      });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }
    if (e.target && e.target.closest && e.target.closest('select, input, textarea')) return;
    if (e.key === 'ArrowLeft' && items.length > 1) { e.preventDefault(); step(-1); }
    else if (e.key === 'ArrowRight' && items.length > 1) { e.preventDefault(); step(1); }
  }

  /* ---------- trigger ---------- */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest(TRIGGER);
    if (!trigger) return;
    if (e.defaultPrevented) return;
    // Let the browser handle new-tab / new-window / download intents.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
    if (!handleFromUrl(trigger.getAttribute('href'))) return;  // not a product URL: navigate
    e.preventDefault();
    try {
      if (!open(trigger)) window.location.href = trigger.getAttribute('href');
    } catch (err) {
      // Never trap the shopper: fall back to the real product page.
      var href = trigger.getAttribute('href');
      if (href) window.location.href = href;
    }
  });
})();
